import { type IViewsKeys } from "@domain/models/User";
import { type ProductKey } from "@domain/models/Product";
import { child, get, set, update as fbUpdate } from "firebase/database";
import { refSettings } from "@domain/api/firebase/firebaseRootRefs";
import { type ITableColumns } from "@domain/models/dms";

const DmsColumnWidthsKey = "dmsColumnWidths";
export class ProgramSettings {
    hideBuchText?: boolean;
    useBuchText2?: boolean;
    useBelegfeld2?: boolean;
    useKs?: boolean;
    useExpertBu?: boolean;
    useCurrency?: boolean;
    useDueDate?: boolean;
    hideContacts?: boolean;
    useVatNumber?: boolean;

    protected static async retrieveFields(companyId: string): Promise<Record<ProductKey, ProgramSettings>> {
        const fbRef = child(refSettings, `${companyId}/global/forms`);
        return get(fbRef)
            .then((s) => s.val() ?? {})
            .catch(() => ({}));
    }

    protected static async retrieveColumns(data: { userId: string; companyId: string }) {
        if (!data?.userId || !data?.companyId) {
            return new Map<keyof IViewsKeys, string[]>();
        }

        const fbRef = child(refSettings, `${data.companyId}/user/${data.userId}/tables`);
        const val: Record<string, any> = await get(fbRef)
            .then((s) => s.val() ?? {})
            .catch(() => ({}));

        return new Map(Object.entries(val)) as Map<keyof IViewsKeys, string[]>;
    }

    static async getFields(companyId: string) {
        return this.retrieveFields(companyId);
    }

    static async getColumns(data: { userId: string; companyId: string }) {
        return this.retrieveColumns(data);
    }

    static async updateFields(companyId: string, productKey: ProductKey, update: Partial<ProgramSettings>) {
        const fbRef = child(refSettings, `${companyId}/global/forms/${productKey}`);
        await fbUpdate(fbRef, update);
    }

    static async updateColumns(
        data: { userId?: string; companyId?: string },
        view: keyof IViewsKeys,
        update: string[],
    ) {
        const fbRef = child(refSettings, `${data.companyId}/user/${data.userId}/tables/${view}`);
        await set(fbRef, update);
    }

    static async getDmsConfig(keys: { userId: string; companyId: string }): Promise<{
        enabledColumns: Record<string, (keyof ITableColumns)[]>;
        columnWidths: Map<string, Map<keyof ITableColumns, number>>;
    }> {
        if (!keys?.userId || !keys?.companyId) {
            return { enabledColumns: {}, columnWidths: new Map() };
        }

        const fbRef = child(refSettings, `${keys.companyId}/user/${keys.userId}/dms_table_columns`);

        const val: Record<string, (keyof ITableColumns)[]> = await get(fbRef)
            .then((s) => s.val() ?? {})
            .catch(() => ({}));

        const columnWidths = new Map<string, Map<keyof ITableColumns, number>>();
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(DmsColumnWidthsKey)) {
                columnWidths.set(key.split(":").pop(), new Map(JSON.parse(localStorage.getItem(key))));
            }
        }

        return {
            enabledColumns: val,
            columnWidths,
        };
    }

    static async updateDmsEnabledColumns(
        data: { userId?: string; companyId?: string },
        typeKey: string,
        enabledColumns: string[],
    ) {
        const fbRef = child(refSettings, `${data.companyId}/user/${data.userId}/dms_table_columns/${typeKey}`);

        await set(fbRef, enabledColumns);
    }

    static updateDmsColumnWidths(typeKey: string, columnWidths: Map<keyof ITableColumns, number>) {
        const key = `${DmsColumnWidthsKey}:${typeKey}`;
        localStorage.setItem(key, JSON.stringify([...columnWidths.entries()]));
    }
}
